import { Injectable } from '@angular/core';
import { ApiService } from '../services/api.service';
import { AuthService } from './auth.service';
import { CryptoStore } from '../state/crypto.store';
import { ITransaction } from '../models/Transactions';
import { WHITELABEL_COMPANY } from '../models/Whitelabel';
import { Utils } from '../utils/Utils';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { RAMP_MODE } from '../models/Auth';
import { Observable } from 'rxjs';
import { EXCHANGE_COUNTRY, PAYMENT_METHOD, TYPE_MINIAPP } from '../models/Onboarding';
import { ACCOUNT_CURRENCY } from '../models/User';
import { ViewService } from 'src/app/modules/shared/services/view.service';
import { IMinOperation } from '../../public/services/utils/public-data';

@Injectable({
  providedIn: 'root',
})
export class CryptoService {
  constructor(
    public authService: AuthService,
    public api: ApiService,
    private cryptoStore: CryptoStore,
    private router: Router,
    private viewService: ViewService,
  ) {}

  public get prices() {
    return this.cryptoStore.cryptoPrices;
  }

  public get blockchains() {
    return this.cryptoStore.blockchains;
  }

  public get cryptoBalance() {
    return this.cryptoStore.cryptoBalance;
  }

  public get allCryptos() {
    return this.cryptoStore.cryptoList.asObservable();
  }

  public get validSwapPairs() {
    return this.cryptoStore.validSwapPairs;
  }

  public get availableSwapCoins() {
    return this.cryptoStore.availableSwapCoins;
  }

  public get stableCryptos() {
    return this.cryptoStore.stableCryptos;
  }

  public get volatilCryptos() {
    return this.cryptoStore.volatilCryptos;
  }

  public get depositableCoins() {
    return this.cryptoStore.depositableCoins;
  }

  get minOperation() {
    return this.cryptoStore.minOperation;
  }

  get errorLimit() {
    return this.cryptoStore.errorLimit;
  }

  getCryptoPrices() {
    return this.cryptoStore.getCryptoPrices();
  }

  getWithdrawableCoins() {
    return this.cryptoStore.withdrawableCoins;
  }

  getByTickerCurr(ticker: any, curr = 1) {
    return this.cryptoStore.getByTickerCurr(ticker, curr);
  }

  // Esta función es medio nefasta hay que mejorar
  updatePortfolioIfZero() {
    const cryptoBalance = this.cryptoBalance.value;
    const cryptoPortfolio = this.authService.myCryptoPortfolio.value;
    const elementZero = cryptoPortfolio.filter(
      (element) =>
        Number(element.balance) === 0 &&
        (!cryptoBalance[element.ticker] || Number(cryptoBalance[element.ticker].amount) === 0) &&
        !cryptoBalance[element.ticker].stakeBalance,
    )[0];
    if (elementZero) {
      const index = cryptoPortfolio.indexOf(elementZero);
      cryptoPortfolio.splice(index, 1);
    }
    this.authService.myCryptoPortfolio.next(cryptoPortfolio);
  }

  getTotalCryptoBalance() {
    return this.cryptoStore.getTotalCryptoBalance();
  }

  getCryptoPriceByCoin(coin: string) {
    return this.cryptoStore.getCryptoPriceByCoin(coin);
  }

  getCryptoPriceByCoinWld(coin: string) {
    return this.cryptoStore.getCryptoPriceByCoinWld(coin);
  }

  changeCryptoPrices(crypto: any) {
    this.cryptoStore.changeCryptoPrices(crypto);
  }

  applyBenefit(newBalance: any) {
    this.cryptoStore.applyBenefit(newBalance);
  }

  swapBetweenCoins(
    // srcCoin: string,
    // srcCoinAmount: number,
    // destCoin: string,
    // destCoinAmount: number,
    amount: string,
    coinPair: string,
    operation: string,
    code: string,
  ) {
    return this.cryptoStore.swapBetweenCoins(amount, coinPair, operation, code);
  }

  getCoinFullName(ticker: string) {
    return this.cryptoStore.getCoinFullName(ticker);
  }

  isStableCoin(ticker: string) {
    return this.cryptoStore.isStableCoin(ticker);
  }

  getValidSwapPair(srcTicker: string, destTicker: string) {
    return this.cryptoStore.getValidSwapPair(srcTicker, destTicker);
  }

  isBuyingSwapPair(srcTicker: string, destTicker: string) {
    return this.cryptoStore.isBuyingSwapPair(srcTicker, destTicker);
  }

  isHiddenInMarket(ticker: string) {
    return this.cryptoStore.isHiddenInMarket(ticker);
  }

  setPortfolioByCryptoBalance() {
    this.cryptoStore.setPortfolioByCryptoBalance();
  }

  lockMarketOrder(coin: string, operation: string, isSwap: boolean = false) {
    return this.cryptoStore.lockMarketOrder(coin, operation, isSwap);
  }

  createMarketOrder(amount: string, coin: string, operation: string, code: string) {
    return this.cryptoStore.createMarketOrder(amount, coin, operation, code);
  }

  updateTransactionToPortfolio(tx: ITransaction, operation: string) {
    this.cryptoStore.updateTransactionToPortfolio(tx, operation);
  }

  sendWithdrawEmailVerification(params: {
    address: string;
    amount: string;
    coin: string;
    chain: string;
    costCode: string;
  }) {
    return this.cryptoStore.sendWithdrawEmailVerification(params);
  }

  withdrawCrypto(code: string) {
    return this.cryptoStore.withdrawCrypto(code);
  }

  getWithdrawCost(ticker: string, wallet: string) {
    return this.cryptoStore.getWithdrawCost(ticker, wallet);
  }

  getBlockchainStatus() {
    return this.cryptoStore.getBlockchainStatus();
  }

  getOrdersStatus() {
    return this.cryptoStore.getOrdersStatus();
  }

  lockPriceWhitelabel(
    coin: 'USDC' | 'WLD' | 'USDT' | 'USDCB',
    amount: number,
    lockType: 'ADDRESS' | 'CUIT' = 'ADDRESS',
    against: ACCOUNT_CURRENCY = ACCOUNT_CURRENCY.ARS,
    chain: number,
    payload: { externalId: string | undefined; withdrawType: string },
  ) {
    return this.cryptoStore.lockPriceWhitelabel(coin, amount, lockType, against, chain, payload);
  }

  /**
   * Same as lockPriceWhitelabel but only using onbardingState and loggedUser
   * @returns
   */
  lockPriceWhitelabelAuto(redirect: boolean = false, userCountry?: EXCHANGE_COUNTRY) {
    const onboardingState = this.authService.onboardingState.value;
    const user = this.authService.loggedUserObs.value;
    if (!onboardingState) return;
    const gasFee = 0.11;
    const {
      coin,
      cryptoAmount,
      fiatAmount,
      successRedirectUrl,
      exchangeCountry,
      network,
      companyId,
      ramp,
      externalId,
      paymentMethod,
      appOrigin,
    } = onboardingState;
    let depositAddress =
      exchangeCountry !== EXCHANGE_COUNTRY.ARG
        ? environment.accumulatorWalletOtherCountries
        : environment.accumulatorWalletOP;
    const payload = externalId
      ? {
          externalId: externalId,
          withdrawType: this.setWithdrawType(paymentMethod),
        }
      : {
          externalId: undefined,
          withdrawType: this.setWithdrawType(paymentMethod),
        };

    const country = userCountry ? userCountry : exchangeCountry;
    const against: ACCOUNT_CURRENCY = Utils.setAgainstByCountry(country as EXCHANGE_COUNTRY);
    const chain = Utils.getNetwork(network);
    const priceCoin = coin + '_' + against;
    let lockType: 'ADDRESS' | 'CUIT' =
      companyId === WHITELABEL_COMPANY.BEETRANSFER || companyId === WHITELABEL_COMPANY.MENLEI
        ? 'CUIT'
        : 'ADDRESS';

    if (ramp === RAMP_MODE.ON) lockType = 'CUIT';

    const amountObs = new Observable<number>((sub) => {
      if (ramp === RAMP_MODE.OFF) {
        if (!this.minOperation()) return;
        if (cryptoAmount < this.minOperation().cashout.min) {
          const error = {
            error: {
              internalStatus: 'MIN_OPERATION',
              min_operation: this.minOperation().cashout.min,
              coin: coin,
            },
          };
          sub.error(error);
          return;
        } else {
          sub.next(cryptoAmount);
        }
      } else {
        if (!this.minOperation()) return;
        if (cryptoAmount < this.minOperation().cashin.min) {
          const error = {
            error: {
              internalStatus: 'MIN_OPERATION',
              min_operation: this.minOperation().cashin.min,
              coin: coin,
            },
          };
          sub.error(error);
          return;
        } else {
          if (lockType === 'CUIT') {
            //lockPriceByLegalId tenes q enviar en amount la cantidad de pesos a depositar ej 10000 AR
            //El monto enviado tiene que estar expresado en FIAT
            if (cryptoAmount) {
              this.getCryptoPriceByCoin(priceCoin).subscribe({
                next: (price) =>
                  exchangeCountry === EXCHANGE_COUNTRY.BRA
                    ? sub.next(cryptoAmount * price.buy * 1.25)
                    : sub.next(cryptoAmount * price.buy + gasFee),
                error: (err) => sub.next(err),
              });
            } else {
              sub.next(fiatAmount);
            }
          } else {
            //lockPriceByAddress tenes q enviar en amount la cantidad x a vender ej 3 WLD
            //El monto enviado tiene que estar expresado en CRYPTO
            if (cryptoAmount) {
              sub.next(cryptoAmount);
            } else {
              this.getCryptoPriceByCoin(priceCoin).subscribe({
                next: (price) => sub.next(fiatAmount / price.buy),
                error: (err) => sub.next(err),
              });
            }
          }
        }
      }
    });

    return new Observable<{
      price: number;
      expires: string;
      totalFee: number;
      networkFee?: number;
      depositAddress?: string;
    }>((sub) => {
      amountObs.subscribe({
        next: (amount: number) => {
          this.lockPriceWhitelabel(coin, amount, lockType, against, chain, payload).subscribe({
            next: (lockData) => {
              if (lockData.legalEntity === 'PONTE') {
                depositAddress = environment.accumulatorWalletPonte;
              }
              let finalCryptoAmount: number | null = null;
              let finalFiatAmount: number | null = null;
              let estimatedFee: number | string | null = null;
              const expiresAt = new Date(parseInt(lockData.expires) * 1000).toISOString();
              if (cryptoAmount) {
                finalCryptoAmount = cryptoAmount + (lockType === 'CUIT' ? gasFee : 0);
                finalFiatAmount = finalCryptoAmount * lockData.price;
              } else {
                finalCryptoAmount = fiatAmount / lockData.price;
                finalFiatAmount = fiatAmount;
              }
              estimatedFee = (finalFiatAmount * 0.01).toFixed(2); // A 10% commission is added by default to be sent in the params to WLD
              if (redirect) {
                setTimeout(() => {
                  if (successRedirectUrl) {
                    let sendPaymentMethod = '';

                    paymentMethod
                      ? (sendPaymentMethod = payload.withdrawType.toLowerCase())
                      : (sendPaymentMethod = 'bank_account');
                    window.location.href = `${successRedirectUrl}&depositAddress=${depositAddress}&cryptoAmount=${finalCryptoAmount}&coinCode=${coin}&fiatAmount=${finalFiatAmount}&fiatCode=${against}&expiresAt=${expiresAt}&paymentMethod=${sendPaymentMethod}&externalId=${externalId}&estimatedFee=${estimatedFee}`;
                    this.authService.logoutWLD();
                  } else if (appOrigin) {
                    Utils.sendMessageToParent(
                      TYPE_MINIAPP.PAY,
                      {
                        coin: coin,
                        amount: finalCryptoAmount,
                        depositAddress: depositAddress,
                        against: against,
                        fiatAmount: finalFiatAmount,
                        country: exchangeCountry,
                      },
                      appOrigin,
                    );
                  } else if (ramp !== RAMP_MODE.ON) {
                    this.router.navigateByUrl('/auth/w-signup/success');
                  }
                }, 1);
              }
              sub.next(lockData);
            },
            error: (error) => sub.error(error),
          });
        },
        error: (error) => sub.error(error),
      });
    });
  }

  setWithdrawType(paymentMethod: string) {
    if (paymentMethod === PAYMENT_METHOD.NEQUI) return 'NEQUI';
    else if (paymentMethod === PAYMENT_METHOD.BREBKEY || paymentMethod === 'BREB') return 'BREB';
    else return 'BANK_ACCOUNT';
  }

  getTransaction(hash: string) {
    return this.cryptoStore.getTransaction(hash);
  }

  getMinOperation(pair: string, company: string) {
    const obs = this.api.getMinOperation(pair, company);
    obs.subscribe({
      next: (minOperation) => {
        this.setMinOperation(minOperation);
      },
      error: () => {
        this.cryptoStore.setErrorLimit(true);
      },
    });
    return obs;
  }

  setMinOperation(minOperation: IMinOperation) {
    this.cryptoStore.setMinOperation(minOperation);
  }
}
